import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Send } from "lucide-react";
import { Badge, Button, ErrorState, Icon, LoadingState, TableEmpty, useToast } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import type { Locale } from "@/lib/i18n/config";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { usePageTitle } from "@/shared/layout/page-title";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { ImpersonateButton } from "@/shared/components/ImpersonateButton";
import { useSellerByIdQuery } from "../services/useSellerByIdQuery";
import { SellerEditForm } from "../components/SellerEditForm";
import { StoreCreateDrawer } from "../components/StoreCreateDrawer";
import { InvitePanel } from "../components/InviteReissue";
import { useInviteReissue } from "../components/useInviteReissue";
import { StoreKindTag } from "../components/ui";

type SellerDetailPageProps = {
  locale: Locale;
  sellerId: string;
};

const IMPERSONATION_ROLES = [
  ADMIN_ROLES.ADMIN,
  ADMIN_ROLES.SUPER_ADMIN,
  ADMIN_ROLES.CUSTOMER_SERVICE,
];

/** A card section head: badge, title, one line on what it holds, an action. */
function CardHead({
  badge,
  title,
  hint,
  action,
  id,
}: {
  badge: string;
  title: ReactNode;
  hint: ReactNode;
  action?: ReactNode;
  id: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "16px 20px",
        borderBottom: "1px solid var(--rd-line-row)",
        flexWrap: "wrap",
      }}
    >
      <Badge id={badge} size={36} />
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, flex: 1, minWidth: 0 }}>
        <h2 id={id} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          {title}
        </h2>
        <span style={{ fontSize: 13, color: "var(--rd-text-icon)" }}>{hint}</span>
      </div>
      {action ?? null}
    </div>
  );
}

export function SellerDetailPage({ locale, sellerId }: SellerDetailPageProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session } = useSessionQuery();
  const { data, isLoading, error, refetch } = useSellerByIdQuery({ sellerId });
  const { data: plansData, isLoading: isPlansLoading } = useSubscriptionsQuery();
  const sellerErrorStatus = getQueryErrorStatus(error);
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const invite = useInviteReissue(sellerId, data?.seller.name ?? "");

  const seller = data?.seller;
  const pageName = t(keys.admin.sellerDetailsTitle).toLocaleLowerCase(locale);
  usePageTitle(
    seller?.name,
    undefined,
    seller ? [seller.email, seller.phone].filter(Boolean).join(" · ") : undefined,
  );

  useEffect(() => {
    if (sellerErrorStatus === 404) {
      navigate(`/${locale}/admin/sellers`, { replace: true });
    }
  }, [locale, navigate, sellerErrorStatus]);

  // Back to where the admin came from (their page of the list), or the list.
  const back = (
    <button
      type="button"
      onClick={() => {
        if (window.history.length > 1) navigate(-1);
        else navigate(`/${locale}/admin/sellers`);
      }}
      style={{
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: 0,
        border: 0,
        background: "none",
        font: "inherit",
        fontSize: 13,
        color: "var(--rd-text-secondary)",
        cursor: "pointer",
      }}
    >
      <Icon icon={ChevronLeft} size={14} className="rd-flip-rtl" />
      {t(keys.common.back)}
    </button>
  );

  if (isLoading || isPlansLoading) {
    return (
      <>
        {back}
        <LoadingState
          title={t(keys.shell.loadingTitle, { page: pageName })}
          sub={t(keys.shell.loadingSub)}
          rows={3}
        />
      </>
    );
  }

  if (sellerErrorStatus === 404) {
    return null;
  }

  if (error || !data) {
    return (
      <>
        {back}
        <ErrorState
          title={t(keys.shell.errorTitle, { page: pageName })}
          sub={t(keys.shell.errorSub)}
          onRetry={() => void refetch()}
          retryLabel={t(keys.shell.retry)}
        />
      </>
    );
  }

  const canImpersonateStore =
    session?.admin?.roles?.some((role) =>
      IMPERSONATION_ROLES.includes(role as (typeof IMPERSONATION_ROLES)[number]),
    ) ?? false;
  const plans = plansData?.items ?? [];

  return (
    <>
      {back}

      <section className="rd-card" style={{ overflow: "hidden", maxWidth: 1040 }} aria-labelledby="seller-details-title">
        <CardHead
          id="seller-details-title"
          badge="team"
          title={t(keys.admin.sellersEditTitle)}
          hint={t("admin.sellerDetailsHint")}
        />
        <div style={{ padding: "12px 20px 16px" }}>
          <SellerEditForm
            sellerId={data.seller.id}
            defaultValues={{
              name: data.seller.name,
              email: data.seller.email ?? "",
              phone: data.seller.phone,
            }}
            extraActions={
              // An invite goes to the seller's email, so it needs one.
              data.seller.email ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Send}
                  loading={invite.isSubmitting}
                  loadingLabel={t("admin.inviteSending")}
                  onClick={() => void invite.reissue()}
                >
                  {t(keys.admin.reissueInvite)}
                </Button>
              ) : null
            }
          />
        </div>
        <InvitePanel
          invite={invite.invite}
          error={invite.error}
          locale={locale}
          onRetry={() => void invite.reissue()}
          retrying={invite.isSubmitting}
        />
      </section>

      <section className="rd-card" style={{ overflow: "hidden", maxWidth: 1040 }} aria-labelledby="seller-stores-title">
        <CardHead
          id="seller-stores-title"
          badge="cart-world"
          title={t(keys.admin.storesTitle)}
          hint={t("admin.sellerStoresHint")}
          action={
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setAdding(true)}>
              {t(keys.admin.createStoreAction)}
            </Button>
          }
        />
        {data.stores.length === 0 ? (
          <TableEmpty
            illustration="admin-empty-table"
            title={t(keys.admin.sellerNoStoresYet)}
            actions={
              <Button variant="primary" size="sm" icon={Plus} onClick={() => setAdding(true)}>
                {t(keys.admin.createStoreAction)}
              </Button>
            }
          >
            {t("admin.sellerStoresEmptySub")}
          </TableEmpty>
        ) : (
          data.stores.map((store) => (
            <div
              key={store.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 20px",
                borderBottom: "1px solid var(--rd-line-row)",
                flexWrap: "wrap",
              }}
            >
              <Badge
                id={store.storeKind === "RESTAURANT" ? "servicebell" : "cart-world"}
                size={28}
                shape="rounded"
              />
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, flex: 1, minWidth: 200 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Link
                    to={`/${locale}/admin/stores/${store.id}`}
                    style={{ fontWeight: 500, color: "var(--rd-text)", textDecoration: "none" }}
                    dir="auto"
                  >
                    {store.storeName}
                  </Link>
                  <StoreKindTag kind={store.storeKind} />
                </span>
                <span style={{ fontSize: 12, color: "var(--rd-text-meta)" }}>
                  <bdi dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                    {store.storeSlug}
                  </bdi>
                  {" • "}
                  {t(keys.common.storeCode)}: <bdi dir="ltr">{store.storeShort}</bdi>
                </span>
              </div>
              {canImpersonateStore ? (
                <ImpersonateButton
                  storeId={store.id}
                  locale={locale}
                  adminRoles={session?.admin?.roles}
                  portal={store.storeKind === "RESTAURANT" ? "restaurant" : "seller"}
                />
              ) : null}
            </div>
          ))
        )}
      </section>

      <StoreCreateDrawer
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(result) => {
          setAdding(false);
          toast.show({
            message: t("admin.businessCreatedToast", { name: result.store.storeName }),
            actionLabel: t(keys.common.view),
            onAction: () => navigate(`/${locale}/admin/stores/${result.store.id}`),
          });
        }}
        sellerId={data.seller.id}
        sellerName={data.seller.name}
        locale={locale}
        plans={plans}
      />
    </>
  );
}
